// TES-1076 — what the "Onboardingtratt" page shows, kept free of React and of
// runtime imports so it can be unit-tested with plain `node --test`.
import type {
  AppDownloadReminderCohort,
  AppDownloadReminderKey,
  BankIdDeviceClassKey,
  BankIdPersons,
  BankIdStartMethodKey,
  CohortChannel,
  CohortDimension,
  CohortStepKey,
  F01VersionBucket,
  MeasuredSignal,
  MeasurementStart,
  OnboardingCohortReport,
  OnboardingFunnelBreakdownKey,
  OnboardingFunnelReport,
  OnboardingFunnelStepKey,
  AppHandoffKind,
  EventPlatform,
  OriginChannel,
  OriginSource,
  SegmentFunnel,
  SegmentFunnelStepKey,
} from "@care-by-ella/contracts";

/** Emelie's wording for each step, numbered as in her list (TES-1092's in-app steps after step 8). */
export const STEP_LABEL: Record<OnboardingFunnelStepKey, string> = {
  questions_submitted: "Frågorna inskickade",
  bankid_started: "BankID påbörjat",
  bankid_completed: "BankID genomfört",
  email: "E-post tillagd / verifierad",
  // TES-1167 — the SSR event proves the server rendered the page, not that
  // she saw it; "visad" is the segment funnel's step 3.
  feedback_shown: "Personlig återkoppling renderad (webben)",
  app_handoff_shown: "Appöverlämning visad",
  app_button_clicked: "Appknapp klickad",
  app_first_open: "Appen öppnad första gången",
  app_bankid_login: "Inloggning i appen med BankID",
  app_first_login: "Första inloggning i appen",
  f01_result_shown: "F-01-resultat visat i appen",
  booking_slots_opened: "Bokningsvyn öppnad",
  booking_started: "Bokning påbörjad",
  payment_sheet_opened: "Betalningsfönstret öppnat",
  booking_completed: "Bokning slutförd",
};

export const BREAKDOWN_LABEL: Record<OnboardingFunnelBreakdownKey, string> = {
  web: "Webben",
  web_not_identified: "varav utan BankID",
  app: "Appen",
  email_added: "Tillagd",
  // TES-1202 — beside the step, not part of it.
  app_not_identified:
    "Installationer utan inloggning (granskning/robotar m.m.), ingår inte",
};

/** What a step's main number means, where it needs saying. */
export const STEP_NOTE: Partial<Record<OnboardingFunnelStepKey, string>> = {
  bankid_started:
    "Unika nya patienter, den dag de först påbörjade BankID. Ett försök som aldrig slutförs saknar identitet och räknas bara i diagnostiken under tabellen.",
  bankid_completed: "Nya patientkonton.",
  email: "Antalet är verifierade adresser.",
  feedback_shown:
    "Webbens server bestämde att visa resultatsidan. Det bevisar inte att den syntes på skärmen – ”visad” finns under Kohortkonvertering, per startkanal.",
  app_first_open:
    "Första gången appen öppnades på en ny installation, räknat per person när hon loggat in på den. En installation där ingen loggat in ingår inte – efter varje butiksuppladdning installerar Apples granskning och Google Plays testrobotar appen utan att logga in. De visas för sig bredvid.",
  app_bankid_login: "Första slutförda BankID som startades i appen.",
  app_first_login:
    "Första spåret av appen: notisbehörighet, ett inskickat formulär, ett steg i appen eller BankID i appen.",
  f01_result_shown:
    "Appen hämtade hennes F-01-resultat – på resultatsidan, eller i bokningsvyn som läser det först.",
  booking_slots_opened: "Appen hämtade lediga tider till bokningsvyn.",
  payment_sheet_opened:
    "Hon tryckte på Betala och godkände villkoren; betalningsfönstret öppnas direkt efter.",
  booking_started:
    "Betalsteget med vårdmötets pris öppnat, eller ”Boka vårdmöte” valt efter ett stopp.",
  booking_completed: "Första vårdmötet bokat.",
};

export type FunnelRow = {
  key: OnboardingFunnelStepKey;
  number: number;
  label: string;
  count: number;
  /** Share of the previous step, in whole percent; null for step 1 or a zero. */
  fromPrevious: number | null;
  breakdown: { label: string; count: number }[];
  note: string | null;
  /** TES-1096 — when this step's measurement started, if it is recent. */
  measured: MeasuredNote | null;
};

// ── TES-1096: "Mäts sedan [datum]" ────────────────────────────────────────

/** The signal behind each step whose measurement started after launch. */
const STEP_SIGNAL: Partial<
  Record<OnboardingFunnelStepKey | CohortStepKey, MeasuredSignal>
> = {
  // TES-1109 — the cohort's e-mail steps, read from their audit rows.
  email_added: "email_captured",
  email_verified: "email_verified",
  feedback_shown: "feedback_shown",
  app_handoff_shown: "app_handoff_shown",
  app_button_clicked: "app_button_clicked",
  // TES-1105 — the app's first-open report (app 2.0.5).
  app_first_open: "app_first_open",
  app_bankid_login: "app_bankid",
  f01_result_shown: "f01_result_shown",
  booking_slots_opened: "booking_slots_opened",
  payment_sheet_opened: "payment_sheet_opened",
  // The first app trace existed before; BankID in the app is the part
  // that needs no notification permission, and it is newer.
  app_first_login: "app_bankid",
};

/** Steps where `app_bankid` dates only one of several signals. */
const PARTIAL_STEPS: ReadonlySet<string> = new Set(["app_first_login"]);

/** TES-1167 — the signal behind each segment-funnel step, where one is newer. */
const SEGMENT_STEP_SIGNAL: Partial<
  Record<SegmentFunnelStepKey, MeasuredSignal>
> = {
  email_verified: "email_verified",
  feedback_visible: "feedback_visible",
  app_handoff_used: "app_button_clicked",
  app_first_open: "app_first_open",
  app_bankid_login: "app_bankid",
};

export type MeasuredNote = {
  text: string;
  /** True when the measurement started after the period's first day, so
   * the period's zeros before it are not drop-off. */
  afterStart: boolean;
};

export function measuredNote(
  step: OnboardingFunnelStepKey | CohortStepKey,
  measured: MeasurementStart[] | undefined,
  periodFrom: string,
): MeasuredNote | null {
  return signalNote(
    STEP_SIGNAL[step],
    PARTIAL_STEPS.has(step),
    measured,
    periodFrom,
  );
}

function signalNote(
  signal: MeasuredSignal | undefined,
  partial: boolean,
  measured: MeasurementStart[] | undefined,
  periodFrom: string,
): MeasuredNote | null {
  if (!signal || !measured) return null;
  const entry = measured.find((m) => m.key === signal);
  if (!entry) return null;
  if (entry.since === null) {
    return {
      text: partial
        ? "BankID i appen räknas inte ännu."
        : "Mäts inte ännu – inga händelser registrerade.",
      afterStart: true,
    };
  }
  return {
    text: partial
      ? `BankID i appen räknas sedan ${entry.since}.`
      : `Mäts sedan ${entry.since}.`,
    afterStart: entry.since > periodFrom,
  };
}

export function funnelRows(report: OnboardingFunnelReport): FunnelRow[] {
  return report.steps.map((step, i) => {
    const previous = i > 0 ? report.steps[i - 1]!.count : 0;
    return {
      key: step.key,
      number: i + 1,
      label: STEP_LABEL[step.key],
      count: step.count,
      fromPrevious:
        i > 0 && previous > 0
          ? Math.round((step.count / previous) * 100)
          : null,
      breakdown: step.breakdown.map((b) => ({
        label: BREAKDOWN_LABEL[b.key],
        count: b.count,
      })),
      note: STEP_NOTE[step.key] ?? null,
      measured: measuredNote(step.key, report.measuredSince, report.from),
    };
  });
}

// TES-1096 — BankID by person, attempts as diagnostics.
//
// TES-1622 — no "unique persons who started" row. Only a completed attempt
// carries an identity, so that count could never differ from the completed
// one; the section's note already says why. The API still sends
// `personsStarted` (the contract is unchanged), it is just not shown.
export function bankIdPersonRows(
  bankId: BankIdPersons | undefined,
): { key: keyof BankIdPersons; label: string; count: number }[] {
  if (!bankId) return [];
  return [
    {
      key: "personsCompleted",
      label: "Unika personer som slutfört",
      count: bankId.personsCompleted,
    },
    {
      key: "attempts",
      label: "Försök totalt (diagnostik)",
      count: bankId.attempts,
    },
    {
      key: "attemptsNeverCompleted",
      label: "varav aldrig slutförda, utan identitet (diagnostik)",
      count: bankId.attemptsNeverCompleted,
    },
  ];
}

// TES-1096 — the reminders scheduled in the period, followed forward.
export function reminderFollowRows(
  report: OnboardingFunnelReport,
): { key: keyof AppDownloadReminderCohort; label: string; count: number }[] {
  const r = report.appDownloadReminder?.scheduledInPeriod;
  if (!r) return [];
  const days = report.appDownloadReminder.loginWindowDays;
  const label: Record<keyof AppDownloadReminderCohort, string> = {
    scheduled: "Schemalagda under perioden",
    sent: "varav skickade",
    cancelled: "varav avbrutna (appen öppnades först)",
    pending: "varav väntar fortfarande",
    appOpenedAfterSent: `varav öppnade appen inom ${days} dagar efter mejlet`,
    alreadyInAppWhenSent: "varav hade redan använt appen när mejlet gick",
    clicked: "varav klickade på mejlets knapp",
    sentFromEarlier: "Skickade under perioden men schemalagda före den",
  };
  return (Object.keys(label) as (keyof AppDownloadReminderCohort)[]).map(
    (key) => ({ key, label: label[key], count: r[key] }),
  );
}

// ── TES-1096: "Kohortkonvertering" ────────────────────────────────────────

/** Emelie's cohort steps (TES-1093 point 1), in her words. */
export const COHORT_STEP_LABEL: Record<CohortStepKey, string> = {
  bankid_completed: "Slutförde BankID",
  // TES-1109 — Emelie's two e-mail steps (2026-10-05).
  email_added: "E-post tillagd",
  email_verified: "E-post verifierad",
  // TES-1167 — the SSR event: rendered, not seen.
  feedback_shown: "Återkopplingen renderad på webben",
  app_handoff_shown: "Nådde appöverlämningen",
  // TES-1105 — Emelie's three app-transition steps (2026-10-05).
  app_button_clicked: "Klickat på länken till App Store/Google Play",
  app_first_open: "Öppnat appen första gången",
  app_bankid_login: "Loggat in i appen med BankID",
  booking_started: "Påbörjade en bokning",
  payment_sheet_opened: "Öppnade betalningsfönstret",
  booking_completed: "Bokade och betalade",
};

export const COHORT_CHANNEL_LABEL: Record<CohortChannel, string> = {
  web: "Började på webben",
  app: "Började i appen",
};

export const COHORT_DIMENSION_LABEL: Record<CohortDimension, string> = {
  entry_source: "Källa / kampanj",
  device: "Enhet vid BankID",
  handoff: "Väg till appen",
};

const COHORT_KEY_LABEL: Record<CohortDimension, Record<string, string>> = {
  entry_source: {
    ig: "Instagram",
    campaign: "Annan kampanj (UTM)",
    none: "Ingen kampanj",
    app: "Appen",
  },
  device: { mobile: "Mobil", desktop: "Dator", unknown: "Okänd enhet" },
  handoff: {
    web: "Webbens knapp / QR-kod",
    email: "Påminnelsemejlet",
    ig: "Instagram-länk",
    none: "Inget registrerat klick",
  },
};

export function cohortKeyLabel(
  dimension: CohortDimension,
  key: string,
): string {
  return COHORT_KEY_LABEL[dimension][key] ?? key;
}

/** Whole percent of `of`; null when the group is empty. */
export function percentOf(count: number, of: number): number | null {
  return of > 0 ? Math.round((count / of) * 100) : null;
}

export type CohortStepRow = {
  key: CohortStepKey;
  label: string;
  measured: MeasuredNote | null;
  cells: { channel: CohortChannel; count: number; percent: number | null }[];
};

export function cohortRows(report: OnboardingCohortReport): CohortStepRow[] {
  const steps = report.channels[0]?.steps.map((s) => s.key) ?? [];
  return steps.map((key) => ({
    key,
    label: COHORT_STEP_LABEL[key],
    measured: measuredNote(key, report.measuredSince, report.from),
    cells: report.channels.map((c) => {
      const count = c.steps.find((s) => s.key === key)?.count ?? 0;
      return { channel: c.channel, count, percent: percentOf(count, c.size) };
    }),
  }));
}

// TES-1323 — the cohort by the F-01 version of her newest answer set, so the
// introduction of "Önskat stöd" (v11) can be measured. Drop-off is read per
// step; a person on v10 is never counted as not having answered.
export const F01_VERSION_BUCKET_LABEL: Record<F01VersionBucket, string> = {
  before_desired_support: "F-01 v10 eller tidigare",
  with_desired_support: "F-01 v11 eller senare",
  no_f01: "Ingen F-01",
};

export type F01VersionRow = {
  key: CohortStepKey;
  label: string;
  cells: { bucket: F01VersionBucket; count: number; percent: number | null }[];
};

/** Rows per cohort step, one column per bucket; [] for an older API. */
export function f01VersionRows(
  report: OnboardingCohortReport,
): F01VersionRow[] {
  const buckets = report.f01Versions ?? [];
  const steps = buckets[0]?.steps.map((s) => s.key) ?? [];
  return steps.map((key) => ({
    key,
    label: COHORT_STEP_LABEL[key],
    cells: buckets.map((b) => {
      const count = b.steps.find((s) => s.key === key)?.count ?? 0;
      return { bucket: b.bucket, count, percent: percentOf(count, b.size) };
    }),
  }));
}

export type CohortBreakdownRow = {
  label: string;
  size: number;
  openWindows: number;
  cells: { key: CohortStepKey; count: number; percent: number | null }[];
};

export type CohortBreakdownTable = {
  dimension: CohortDimension;
  channel: CohortChannel;
  title: string;
  rows: CohortBreakdownRow[];
};

/** One table per dimension and channel, groups in the API's order. */
export function cohortBreakdownTables(
  report: OnboardingCohortReport,
): CohortBreakdownTable[] {
  const out: CohortBreakdownTable[] = [];
  for (const row of report.breakdown) {
    let table = out.find(
      (t) => t.dimension === row.dimension && t.channel === row.channel,
    );
    if (!table) {
      table = {
        dimension: row.dimension,
        channel: row.channel,
        title: `${COHORT_DIMENSION_LABEL[row.dimension]} – ${COHORT_CHANNEL_LABEL[row.channel].toLowerCase()}`,
        rows: [],
      };
      out.push(table);
    }
    table.rows.push({
      label: cohortKeyLabel(row.dimension, row.key),
      size: row.size,
      openWindows: row.openWindows,
      cells: row.steps.map((s) => ({
        key: s.key,
        count: s.count,
        percent: percentOf(s.count, row.size),
      })),
    });
  }
  return out;
}

// TES-1084 — the app-download reminder's counts, shown under steps 7–8.
export function reminderRows(
  report: OnboardingFunnelReport,
): { key: AppDownloadReminderKey; label: string; count: number }[] {
  const r = report.appDownloadReminder;
  // An API older than the page sends no reminder block.
  if (!r) return [];
  const label: Record<AppDownloadReminderKey, string> = {
    scheduled: "Påminnelser schemalagda",
    sent: "Påminnelser skickade",
    cancelled: "Avbrutna innan de skickades",
    app_login_after_sent: `Första inloggning i appen inom ${r.loginWindowDays} dagar efter påminnelsen`,
    clicked_from_email: "Klick på mejlets appknapp",
  };
  // The order shown; `label` being a full Record keeps it complete.
  const order: AppDownloadReminderKey[] = [
    "scheduled",
    "sent",
    "cancelled",
    "app_login_after_sent",
    "clicked_from_email",
  ];
  return order.map((key) => ({
    key,
    label: label[key],
    count: r.totals[key],
  }));
}

// TES-1088 — BankID attempts per start method and device class.
const METHOD_LABEL: Record<BankIdStartMethodKey, string> = {
  qr: "Webben, QR-kod",
  // TES-1622 — proposed copy, raised as an open question in the PR.
  autostart: "Webben, BankID öppnas direkt",
  same_device: "Webben, BankID på samma enhet",
  app: "Appen",
  unrecorded: "Före mätningen",
};
const DEVICE_LABEL: Record<BankIdDeviceClassKey, string> = {
  mobile: "mobil",
  desktop: "dator",
  unknown: "okänd enhet",
};

export type BankIdMethodRow = {
  key: string;
  label: string;
  started: number;
  completed: number;
  /** Completed share in whole percent; null when nothing started. */
  completedShare: number | null;
};

export function bankIdMethodRows(
  report: OnboardingFunnelReport,
): BankIdMethodRow[] {
  // An API older than the page sends no method block.
  return (report.bankIdStartMethods ?? []).map((m) => ({
    key: `${m.method}:${m.deviceClass}`,
    label:
      m.method === "unrecorded"
        ? METHOD_LABEL.unrecorded
        : `${METHOD_LABEL[m.method]} – ${DEVICE_LABEL[m.deviceClass]}`,
    started: m.started,
    completed: m.completed,
    completedShare:
      m.started > 0 ? Math.round((m.completed / m.started) * 100) : null,
  }));
}

/** Today's date in Stockholm, YYYY-MM-DD. */
export function stockholmToday(now: Date = new Date()): string {
  // sv-SE formats a date as YYYY-MM-DD.
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** The Stockholm day `days - 1` days before `to`, so the range spans `days`. */
export function rangeEndingAt(
  to: string,
  days: number,
): { from: string; to: string } {
  const d = new Date(`${to}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return { from: d.toISOString().slice(0, 10), to };
}

/** Why a picked range cannot be asked for, or null when it can. */
export function rangeError(from: string, to: string): string | null {
  if (!from || !to) return "Välj både start- och slutdatum.";
  if (from > to) return "Startdatumet ligger efter slutdatumet.";
  const days =
    (new Date(`${to}T00:00:00.000Z`).getTime() -
      new Date(`${from}T00:00:00.000Z`).getTime()) /
      86_400_000 +
    1;
  if (days > 400) return "Välj högst 400 dagar.";
  return null;
}

// ── TES-1167: the six steps per origin segment ────────────────────────────

/** Emelie's six steps (2026-10-08), in her words and order. */
export const SEGMENT_STEP_LABEL: Record<SegmentFunnelStepKey, string> = {
  email_verified: "1. E-post verifierad",
  feedback_generated: "2. Personlig återkoppling genererad",
  feedback_visible: "3. Personlig återkoppling visad",
  app_handoff_used: "4. Appöverlämningslänk, QR-kod eller appbutikslänk använd",
  app_first_open: "5. Appen öppnad första gången",
  app_bankid_login: "6. Inloggad i appen med BankID",
};

export const ORIGIN_LABEL: Record<OriginChannel, string> = {
  "web-desktop": "Webb desktop",
  "web-mobile": "Webb mobil",
  ios: "App iOS",
  android: "App Android",
  "web-unknown": "Webb, enhet okänd",
  "app-unknown": "App, plattform okänd",
  unknown: "Startkanal okänd",
};

export const ORIGIN_SOURCE_LABEL: Record<OriginSource, string> = {
  lead_ua: "webbformulärets enhet",
  lead_bankid_ua: "webbformulär, enheten från första BankID",
  lead_only: "webbformulär, enhet okänd",
  bankid_web_ua: "första BankID på webben",
  app_install: "appinstallationens plattform",
  push_token: "notistokenets plattform",
  app_no_platform: "appstart, plattform okänd",
  none: "inget att härleda från",
};

export const HANDOFF_KIND_LABEL: Record<AppHandoffKind, string> = {
  qr: "QR-kod",
  handoff_link: "”Fortsätt i Ella-appen”",
  store_link: "appbutikslänk",
  open_app: "”Öppna appen”",
  email_link: "påminnelsemejlets länk",
};

export const PLATFORM_LABEL: Record<EventPlatform, string> = {
  "web-desktop": "webb desktop",
  "web-mobile": "webb mobil",
  ios: "iOS",
  android: "Android",
  unknown: "okänd",
};

const isAppOrigin = (origin: OriginChannel) =>
  origin === "ios" || origin === "android" || origin === "app-unknown";

export type SegmentStepRow = {
  key: SegmentFunnelStepKey;
  label: string;
  measured: MeasuredNote | null;
  cells: {
    origin: OriginChannel;
    count: number;
    /** Of everyone in the segment. */
    percent: number | null;
    /** Step 4 for an app start: her result is shown in the app itself. */
    notApplicable: boolean;
  }[];
};

/** The segment table: one row per step, one cell per segment. */
export function segmentRows(report: OnboardingCohortReport): SegmentStepRow[] {
  const segments = report.segments ?? [];
  const keys = segments[0]?.steps.map((s) => s.key) ?? [];
  return keys.map((key) => ({
    key,
    label: SEGMENT_STEP_LABEL[key],
    measured: signalNote(
      SEGMENT_STEP_SIGNAL[key],
      false,
      report.measuredSince,
      report.from,
    ),
    cells: segments.map((s) => {
      const count = s.steps.find((x) => x.key === key)?.count ?? 0;
      return {
        origin: s.origin,
        count,
        percent: percentOf(count, s.size),
        notApplicable: key === "app_handoff_used" && isAppOrigin(s.origin),
      };
    }),
  }));
}

export type SegmentConversion = {
  origin: OriginChannel;
  label: string;
  verified: number;
  visible: number;
  percent: number | null;
};

/** "E-post verifierad → Personlig återkoppling visad", per segment. */
export function segmentConversions(
  report: OnboardingCohortReport,
): SegmentConversion[] {
  return (report.segments ?? []).map((s) => ({
    origin: s.origin,
    label: ORIGIN_LABEL[s.origin],
    verified: s.verifiedToVisible.verified,
    visible: s.verifiedToVisible.visible,
    percent: percentOf(
      s.verifiedToVisible.visible,
      s.verifiedToVisible.verified,
    ),
  }));
}

/** What each segment's numbers rest on, in words. Empty segments say nothing. */
export function segmentNotes(
  segment: SegmentFunnel,
): { key: string; text: string }[] {
  if (segment.size === 0) return [];
  const out: { key: string; text: string }[] = [];
  const v = segment.visibleBy;
  const parts = [
    v.web_visible > 0 ? `${v.web_visible} på webben (synlig på skärmen)` : "",
    v.app_visible > 0 ? `${v.app_visible} i appen (synlig på skärmen)` : "",
    v.app_fetch_proxy > 0
      ? `${v.app_fetch_proxy} i appen enligt PROXY – appen hämtade resultatet, skärmen mäts först från app 2.0.8`
      : "",
  ].filter(Boolean);
  if (parts.length > 0) {
    out.push({ key: "visible", text: `Visad: ${parts.join(", ")}.` });
  }
  if (segment.webRendered > 0) {
    out.push({
      key: "rendered",
      text: `Renderad av webbens server: ${segment.webRendered} (diagnostik, inte ”visad”).`,
    });
  }
  if (segment.handoffKinds.length > 0) {
    out.push({
      key: "handoff",
      text: `Steg 4: ${segment.handoffKinds
        .map((k) => `${HANDOFF_KIND_LABEL[k.kind]} ${k.count}`)
        .join(", ")}.`,
    });
  }
  const where = segment.steps
    .filter((s) => s.platforms.length > 0)
    .map(
      (s) =>
        `${SEGMENT_STEP_LABEL[s.key].split(".")[0]}: ${s.platforms
          .map((p) => `${PLATFORM_LABEL[p.platform]} ${p.count}`)
          .join(", ")}`,
    );
  if (where.length > 0) {
    out.push({
      key: "platform",
      text: `Var det hände – steg ${where.join("; steg ")}.`,
    });
  }
  return out;
}
